import { useRef, useCallback } from 'react'
import { useJsApiLoader, Autocomplete } from '@react-google-maps/api'
import { Input } from '@/components/ui/input'
import { Loader2, MapPin } from 'lucide-react'
import { GOOGLE_MAPS_LOADER_OPTIONS } from '@/lib/maps/loader'

export interface ZipHubSelection {
  name: string
  address: string
  lat: number
  lng: number
}

interface ZipHubInputProps {
  onSelect: (hub: ZipHubSelection) => void
  placeholder?: string
  disabled?: boolean
}

/**
 * Address/place autocomplete for adding a ZIP-pricing hub.
 *
 * Unlike the deadhead HubAddressInput (which restricts to `types: ['address']`),
 * this allows establishments too, so airports like "Hartsfield-Jackson" resolve.
 * On selection it reports the place name + formatted address + coordinates.
 */
export function ZipHubInput({
  onSelect,
  placeholder = 'Search an airport or address to add as a hub',
  disabled = false,
}: ZipHubInputProps) {
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null)
  const inputRef = useRef<HTMLInputElement | null>(null)

  // Options MUST match every other Builder useJsApiLoader call site or the
  // loader throws "Loader must not be called again with different options".
  const { isLoaded, loadError } = useJsApiLoader(GOOGLE_MAPS_LOADER_OPTIONS)

  const onAutocompleteLoad = useCallback(
    (autocomplete: google.maps.places.Autocomplete) => {
      autocompleteRef.current = autocomplete
    },
    []
  )

  const onPlaceChanged = useCallback(() => {
    const autocomplete = autocompleteRef.current
    if (!autocomplete) return

    const place = autocomplete.getPlace()
    if (!place.geometry?.location) {
      console.warn('No location data for selected place')
      return
    }

    const lat = place.geometry.location.lat()
    const lng = place.geometry.location.lng()
    const address = place.formatted_address || ''
    const name = place.name || address

    onSelect({ name, address, lat, lng })

    // Clear the input so the next hub can be searched
    if (inputRef.current) inputRef.current.value = ''
  }, [onSelect])

  if (loadError) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <span>Failed to load Google Maps. Please check your API key.</span>
      </div>
    )
  }

  if (!isLoaded) {
    return (
      <div className="flex items-center gap-2">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span className="text-sm text-muted-foreground">Loading address input...</span>
      </div>
    )
  }

  return (
    <Autocomplete
      onLoad={onAutocompleteLoad}
      onPlaceChanged={onPlaceChanged}
      options={{ componentRestrictions: { country: 'us' } }}
    >
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          ref={inputRef}
          type="text"
          placeholder={placeholder}
          disabled={disabled}
          className="pl-9"
        />
      </div>
    </Autocomplete>
  )
}
